import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, ChevronDown, ChevronUp } from 'lucide-react';
import { useApp } from '../contexts/AppContext';

const DraggablePanel = ({ id, title, icon: Icon, children, defaultCollapsed = false, accentColor = '#00d4ff', className = '', headerRight = null }) => {
  const { t } = useApp();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
    opacity: isDragging ? 0.45 : 1,
    zIndex: isDragging ? 999 : 'auto',
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} className={className}>
      <div
        className="rounded-2xl overflow-hidden h-full"
        style={{
          background: 'var(--color-card)',
          border: `1px solid ${isDragging ? accentColor + '60' : 'var(--border-dim)'}`,
          boxShadow: isDragging ? `0 20px 60px rgba(0,0,0,0.6), 0 0 0 2px ${accentColor}40` : 'none',
          transition: 'box-shadow 150ms ease, border-color 150ms ease',
        }}
      >
        <div
          className="flex items-center gap-3 px-4 select-none"
          style={{
            height: 40,
            borderBottom: collapsed ? 'none' : '1px solid var(--border-lo)',
            background: isDragging ? 'var(--tint)' : 'transparent',
          }}
        >
          <div
            {...attributes}
            {...listeners}
            className="flex items-center justify-center rounded-lg transition-all hover:bg-white/5 shrink-0"
            style={{ width: 28, height: 28, cursor: isDragging ? 'grabbing' : 'grab', color: isDragging ? accentColor : 'var(--text-mute)' }}
            title={t('drag_handle')}
          >
            <GripVertical size={14} />
          </div>

          {Icon && <Icon size={13} color={accentColor} />}
          <p className="text-[9px] font-black uppercase tracking-widest flex-1"
            style={{ color: 'var(--text-dim)', fontFamily: 'IBM Plex Mono' }}>
            {title}
          </p>

          {headerRight && <div className="shrink-0">{headerRight}</div>}

          <button
            onClick={() => setCollapsed(c => !c)}
            className="flex items-center justify-center rounded-lg transition-all hover:bg-white/5 shrink-0"
            style={{ width: 26, height: 26 }}
            title={collapsed ? t('panel_open') : t('panel_close')}
          >
            {collapsed
              ? <ChevronDown size={12} color="var(--text-mute)" />
              : <ChevronUp size={12} color="var(--text-mute)" />
            }
          </button>
        </div>

        {!collapsed && <div>{children}</div>}
      </div>
    </div>
  );
};

export default DraggablePanel;
